import dayjs from 'dayjs';
import { memo } from 'react';
import { useTheme } from 'styled-components';

import Tooltip from '~components/common/Tooltip';
import ExclamationMark from '~components/Icon/ExclamationMark';
import LoadingCircle from '~components/Loading';
import { COMPANY_DETAIL_MENU } from '~constant/company';
import BlockedInfo from '~features/company/BlockedInfo';
import Card from '~features/company/Card';
import DropDownSource from '~features/company/DropDownSource';
import ErrorSection from '~features/company/ErrorSection';
import Salary from '~features/company/SalaryInfo/Salary';
import SalarySummary from '~features/company/SalaryInfo/SalarySummary';
import SuggestionCompany from '~features/company/SalaryInfo/SuggestionCompany';
import WikiSalary from '~features/company/SalaryInfo/WikiSalary';
import useGetCompanyInfo from '~hooks/query/company/useCompanyInfo';
import useGetCompanySalaryQuery from '~hooks/query/company/useCompanySalary';
import useDropdownSelect from '~hooks/useDropdownSelect';
import { useRouterQuery } from '~hooks/useRouterQuery';
import { ValueOf } from '~types/common';
import { COMPANY_SOURCE, COMPANYS_SOURCE_VALUE, CompanyStates } from '~types/company';
import { setGtmDataLayer } from '~utils/common/thirdPartyEvent';

import CompanySectionTitle from '../CompanySectionTitle';
import SalaryChart from './SalaryChart';
import { S } from './style';

function SalaryInfo({
  registerRef,
  companyStates,
}: {
  registerRef: (element: HTMLElement, id: ValueOf<typeof COMPANY_DETAIL_MENU>) => void;
  companyStates: CompanyStates;
}) {
  const theme = useTheme();
  const companyId = useRouterQuery('companyId');
  const { selectedOption, handleSelectChange } = useDropdownSelect<COMPANYS_SOURCE_VALUE>();
  const { data: companyInfo } = useGetCompanyInfo(companyId);
  const isSalaryBlocked = !companyStates.salary;
  const isClosed = companyStates.closed;

  const salaryQuery = useGetCompanySalaryQuery(
    companyId,
    isSalaryBlocked,
    isClosed,
    selectedOption,
  );

  const cardProps = {
    title: '연봉',
    sectionId: COMPANY_DETAIL_MENU.SALARY,
    registerRef,
    rightCardHeader: !(
      companyStates.deleted ||
      companyStates.closed ||
      !salaryQuery.data ||
      salaryQuery.isError
    ) && (
      <DropDownSource
        value={selectedOption || COMPANY_SOURCE[salaryQuery.data.defaultSource]}
        options={salaryQuery.data.sourceOptions}
        emptyValue={
          !(selectedOption || COMPANY_SOURCE[salaryQuery.data.defaultSource])
            ? '정보없음'
            : undefined
        }
        tooltipHelperText={
          salaryQuery.data.salaryGraphData[0].updatedAt
            ? `${dayjs(salaryQuery.data.salaryGraphData[0].updatedAt).format('YYYY.MM')} 기준 :`
            : '연봉 출처 : '
        }
        tooltipText='출처의 데이터를 기반으로 추정된 값으로, 참고 자료로만 활용하시길 바랍니다.'
        onChange={(value: string) => {
          setGtmDataLayer('company__salarySource__click', {
            gnbKind: '연봉',
            from: selectedOption || COMPANY_SOURCE[salaryQuery.data.defaultSource],
            to: value,
          });
          handleSelectChange(value as COMPANYS_SOURCE_VALUE);
        }}
      />
    ),
  };
  if (companyStates?.closed || companyStates?.deleted) {
    return null;
  }

  if (isSalaryBlocked) {
    return (
      <Card {...cardProps}>
        <BlockedInfo />
      </Card>
    );
  }

  if (salaryQuery.isError) {
    return (
      <Card {...cardProps}>
        <ErrorSection />
      </Card>
    );
  }

  if (salaryQuery.isLoading) {
    return (
      <Card {...cardProps}>
        <LoadingCircle />
      </Card>
    );
  }

  return (
    <Card {...cardProps}>
      {salaryQuery.data.salaryGraphData[0] && (
        <SalarySummary salary={salaryQuery.data.salaryGraphData[0]} />
      )}
      <Salary data={salaryQuery.data.salaryGraphData} companyName={companyInfo?.name} />
      <WikiSalary
        id={companyId}
        isSalaryBlocked={isSalaryBlocked}
        selectSource={selectedOption || COMPANY_SOURCE[salaryQuery.data.defaultSource]}
      />
      <S.SalaryChartContainer>
        <CompanySectionTitle inlineCss={S.cssTitleBullet}>
          월별 평균급여
          <Tooltip
            width={215}
            tooltipText={`
              매년 7월은 국민연금 정산월입니다. 본 자료는 국민연금 보험료 납부액을 기반으로 산출하므로 정산 월의 전 월과 평균 급여차이가 크게 날 수 있습니다.
              <br/><br/>
              국민연금 보험료는 기준소득월액에 따라 차등 산정됩니다. 본 기업의 기준소득월액이 상한액을 초과하거나 하한액을 미달한다면 추정에 오차가 발생할 수 있을 수 있습니다. (2022년 상한액 553만 원, 하한액 35만 원)
              `}
            position='center'>
            <ExclamationMark width={15} color={theme.colors.gray700} />
          </Tooltip>
        </CompanySectionTitle>
        <S.SalaryGreyBox>
          <SalaryChart companyId={companyInfo?.PK_NM_HASH as string} />
        </S.SalaryGreyBox>
      </S.SalaryChartContainer>
      <SuggestionCompany />
    </Card>
  );
}

export default memo(SalaryInfo);
